import { useState } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Select from '../../components/ui/Select.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';

const currentYear = String(new Date().getFullYear());

const CARRY_OVER_LABELS = {
  none: 'None \u2014 expires',
  unlimited: 'Unlimited',
  capped: 'Capped',
  expires: 'Expires',
};

export default function RolloverPage() {
  const toast = useToast();
  const [fromYear, setFromYear] = useState(currentYear);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const toYear = String(Number(fromYear) + 1);

  async function handlePreview() {
    setLoading(true);
    setPreview(null);
    try {
      const res = await adminApi.rollover(fromYear, true);
      setPreview(res.preview || []);
    } catch (err) {
      toast.error(`Preview failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleApply() {
    setApplying(true);
    try {
      const res = await adminApi.rollover(fromYear, false);
      toast.success(`Rollover complete: ${res.appliedCount} balances created, ${res.skippedCount} skipped`);
      setShowConfirm(false);
      await handlePreview();
    } catch (err) {
      toast.error(`Rollover failed: ${err.message}`);
    } finally {
      setApplying(false);
    }
  }

  const alreadyRolled = preview?.filter((r) => r.alreadyRolledOver).length || 0;
  const toApply = preview?.filter((r) => !r.alreadyRolledOver).length || 0;
  const totalExpired = preview?.reduce((sum, r) => sum + (r.expired || 0), 0) || 0;
  const totalCarryOver = preview?.reduce((sum, r) => sum + (r.carryOverAmount || 0), 0) || 0;

  const columns = [
    {
      key: 'displayName',
      label: 'Employee',
      render: (r) => (
        <div>
          <div className="font-medium">{r.displayName}</div>
          <div className="text-xs text-muted-foreground">{r.department || '\u2014'}</div>
        </div>
      ),
    },
    { key: 'typeName', label: 'Leave Type' },
    {
      key: 'carryOverRule',
      label: 'Rule',
      render: (r) => (
        <span className="text-xs">{CARRY_OVER_LABELS[r.carryOverRule] || r.carryOverRule}</span>
      ),
    },
    {
      key: 'currentRemaining',
      label: `${fromYear} Remaining`,
      render: (r) => <span className="font-medium">{r.currentRemaining} days</span>,
    },
    {
      key: 'carryOverAmount',
      label: 'Carry Over',
      render: (r) => (
        <span className={r.carryOverAmount > 0 ? 'text-emerald-600' : 'text-muted-foreground'}>
          {r.carryOverAmount > 0 ? `+${r.carryOverAmount}` : '0'} days
        </span>
      ),
    },
    {
      key: 'expired',
      label: 'Expired',
      render: (r) => (
        <span className={r.expired > 0 ? 'text-profun-pink' : 'text-muted-foreground'}>
          {r.expired > 0 ? `-${r.expired}` : '0'} days
        </span>
      ),
    },
    {
      key: 'newTotal',
      label: `${toYear} Total`,
      render: (r) => <span className="font-semibold">{r.newTotal} days</span>,
    },
    {
      key: 'alreadyRolledOver',
      label: 'Status',
      render: (r) => (
        <Badge variant={r.alreadyRolledOver ? 'info' : 'warning'}>
          {r.alreadyRolledOver ? 'Done' : 'Pending'}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Year-End Rollover</h1>
        <p className="text-muted-foreground">
          Apply carry-over rules and create next year's balances for all employees.
        </p>
      </div>

      <div className="mb-6 flex items-end gap-3">
        <Select
          label="Roll over from"
          value={fromYear}
          onChange={(e) => { setFromYear(e.target.value); setPreview(null); }}
          options={[
            { value: '2025', label: '2025 \u2192 2026' },
            { value: '2026', label: '2026 \u2192 2027' },
            { value: '2027', label: '2027 \u2192 2028' },
          ]}
        />
        <Button variant="secondary" onClick={handlePreview} loading={loading}>
          Preview
        </Button>
        {preview && toApply > 0 && (
          <Button onClick={() => setShowConfirm(true)}>
            Apply Rollover
          </Button>
        )}
      </div>

      {preview && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Total Entries" value={preview.length} />
          <StatCard label="To Apply" value={toApply} className="text-amber-600" />
          <StatCard label="Already Done" value={alreadyRolled} className="text-emerald-600" />
          <StatCard label="Days Carried Over" value={totalCarryOver} className="text-primary" />
        </div>
      )}

      {preview ? (
        <DataTable
          columns={columns}
          rows={preview}
          loading={false}
          emptyMessage="No active users or leave types found."
          keyFn={(r) => `${r.userId}_${r.typeId}`}
        />
      ) : !loading ? (
        <div className="rounded-lg border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
          Select a year and click Preview to see what the rollover will do.
        </div>
      ) : null}

      {showConfirm && (
        <Modal
          isOpen={true}
          onClose={() => setShowConfirm(false)}
          title="Confirm Rollover"
          width="480px"
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowConfirm(false)}>Cancel</Button>
              <Button onClick={handleApply} loading={applying}>Apply Rollover</Button>
            </>
          }
        >
          <div className="space-y-3">
            <p className="text-sm">This will create {toYear} balances for {toApply} user-type combinations.</p>
            <p className="text-sm text-muted-foreground">
              {totalCarryOver} days will be carried over. {totalExpired} days will expire.
              {alreadyRolled > 0 && ` ${alreadyRolled} entries already rolled over will be skipped.`}
            </p>
            <p className="text-sm font-medium text-amber-600">
              This action cannot be undone automatically.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatCard({ label, value, className }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className={`text-xl font-bold ${className || 'text-foreground'}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
